import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { getDateRangeBounds } from '../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { GET_RECRUITER_STATS_QUERY } from '../graphql/queries';
import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IRecruiterStatsEntry {
  userId: string;
  firstName: string;
  lastName: string;
  regionId?: string;
  regionName?: string;
  recruitedDonorCount: number;
  activeLocationCount: number;
  totalAssignedLocationCount: number;
  prevRecruitedDonorCount: number;
  prevActiveLocationCount: number;
}

export interface IUnassignedLocationEntry {
  id: string;
  name: string;
  regionName?: string;
}

export interface IUseRecruiterStatsProps {
  dateRange: DateRangeType;
  regionIds?: string[];
  activeLocationThresholdDays: number;
}

export interface IUseRecruiterStatsResult {
  entries: IRecruiterStatsEntry[];
  unassignedLocationCount: number;
  unassignedLocations: IUnassignedLocationEntry[];
  loading: boolean;
  error?: Error;
}

export function useRecruiterStats(
  props: IUseRecruiterStatsProps
): IUseRecruiterStatsResult {
  const { dateRange, regionIds, activeLocationThresholdDays } = props;

  // Compute current and previous period bounds (shift back by period length)
  const { currentBounds, prevBounds } = useMemo(() => {
    const current = getDateRangeBounds(dateRange);
    const durationMs = current.end.getTime() - current.start.getTime();
    const prevEnd = new Date(current.start.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - durationMs);
    return {
      currentBounds: current,
      prevBounds: { start: prevStart, end: prevEnd },
    };
  }, [dateRange]);

  const { data, loading, error } = useQuery(GET_RECRUITER_STATS_QUERY, {
    variables: {
      input: {
        start: currentBounds.start.toISOString(),
        end: currentBounds.end.toISOString(),
        prevStart: prevBounds.start.toISOString(),
        prevEnd: prevBounds.end.toISOString(),
        activeLocationThresholdDays,
        regionIds: regionIds && regionIds.length > 0 ? regionIds : undefined,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const result = useMemo(() => {
    const stats = data?.getRecruiterStats;
    if (!stats) {
      return {
        entries: [],
        unassignedLocationCount: 0,
        unassignedLocations: [],
      };
    }
    return {
      entries: stats.entries as IRecruiterStatsEntry[],
      unassignedLocationCount: stats.unassignedLocationCount as number,
      unassignedLocations:
        stats.unassignedLocations as IUnassignedLocationEntry[],
    };
  }, [data]);

  return {
    ...result,
    loading,
    error: error as Error | undefined,
  };
}
